/**
 * SylviPinSection — minimal helper for the three multi-panel pin sections
 * on the about-us page (timeline, core-values, why-sylvi).
 *
 * The about-us-scroll-engine section provides CSS scroll-snap that handles
 * all inter-section AND inter-panel snapping. The browser guarantees scroll
 * position lands on a panel slot after any user input.
 *
 * This helper's job is just:
 *   • Build the (N+1) × 100vh wrapper around the section's sticky child.
 *   • Watch scroll position; call setActive(i) when the user enters panel i.
 *   • Expose moveToPanel(i) for dot/marker click navigation.
 *
 * No wheel/touch/key hijack. No cooldowns. No gesture-boundary detection.
 * CSS scroll-snap handles all of that natively.
 */
(function () {
  'use strict';

  function SylviPinSection(opts) {
    if (!opts || !opts.section || typeof opts.total !== 'number' ||
        typeof opts.setActive !== 'function' || !opts.globalFlag) {
      console.warn('[PIN] invalid config', opts);
      return null;
    }

    var section    = opts.section;
    var total      = opts.total;
    var setActive  = opts.setActive;
    var globalFlag = opts.globalFlag;

    // Wrap the section in a sticky wrapper if not already wrapped.
    var wrapper = section.parentElement;
    if (!wrapper.classList.contains('sylvi-pin-wrap')) {
      var w = document.createElement('div');
      w.className = 'sylvi-pin-wrap';
      section.parentNode.insertBefore(w, section);
      w.appendChild(section);
      wrapper = w;
    }

    function applyLayout() {
      wrapper.style.position = 'relative';
      wrapper.style.height = ((total + 1) * 100) + 'vh';
      section.style.position = 'sticky';
      section.style.top = '0';
      section.style.height = '100vh';
    }
    applyLayout();
    window.addEventListener('resize', applyLayout);

    function wrapTop() {
      return wrapper.getBoundingClientRect().top + window.scrollY;
    }

    function viewportH() { return window.innerHeight; }

    var current = -1; // -1 forces first setActive call to fire

    function indexFromScroll() {
      var scrolled = window.scrollY - wrapTop();
      if (scrolled <= 0) return 0;
      var max = wrapper.offsetHeight - viewportH();
      if (scrolled >= max) return total - 1;
      // Round to nearest panel slot. Each slot is one viewport tall.
      return Math.max(0, Math.min(total - 1, Math.round(scrolled / viewportH())));
    }

    function isPinned() {
      var r = wrapper.getBoundingClientRect();
      return r.top <= 4 && r.bottom >= viewportH() - 4;
    }

    function syncFromScroll() {
      var pinned = isPinned();
      window[globalFlag] = pinned;
      if (!pinned) return;
      var idx = indexFromScroll();
      if (idx !== current) {
        current = idx;
        setActive(idx);
      }
    }

    // rAF-throttled scroll listener.
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        syncFromScroll();
      });
    }, { passive: true });

    // Pre-render panel 0 so the section's visual state matches its API
    // state BEFORE the user reaches it. Without this, the section sits
    // with no panel highlighted (its default unstyled state) until the
    // first scroll event fires our rAF listener — visible as a flash
    // of "nothing active" when the user enters from above.
    current = 0;
    setActive(0);

    // Initial sync — covers the case where the page loads scrolled into
    // the section already (anchor, restore). Will fix `current` if the
    // user is mid-section.
    syncFromScroll();

    // Public API
    function moveToPanel(i) {
      i = Math.max(0, Math.min(total - 1, i));
      var targetY = wrapTop() + i * viewportH();
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    function setEntryPanel(entry) {
      var idx = entry === 'bottom' ? total - 1 : 0;
      moveToPanel(idx);
    }

    return {
      setEntryPanel: setEntryPanel,
      moveToPanel: moveToPanel,
      getCurrent: function () { return current; },
      total: total,
      wrapper: wrapper,
      section: section
    };
  }

  // Registry so other code (e.g. about-us-scroll-engine) can locate APIs.
  var registry = Object.create(null);

  window.SylviPinSection = {
    create: function (opts) {
      var api = SylviPinSection(opts);
      if (api && opts && opts.sectionId) registry[opts.sectionId] = api;
      return api;
    },
    get: function (id) { return registry[id] || null; }
  };
})();
